import React from "react";
import { TLBaseShape, RecordProps, BaseBoxShapeUtil, T, Editor, TLResizeInfo, useValue } from "@tldraw/tldraw";
import { mixColors } from "./CustomCloudUtil";
import { ShapeUtilHelper } from "./ShapeUtilHelper";
import { shadowsToCssFilterDropShadow } from "./shadowUtils";
import { HOVER_EFFECT } from "./constants";
import { defaultScale } from "../constants";

type CustomSvgUtilType = TLBaseShape<
  "customSvg",
  {
    w: number;
    h: number;
    fill: string;
    outlineColor: string;
    dash: any;
    transparency: number;
    strokeDasharray?: string;
    strokeWidth?: number;
    filterBlur?: number;
    rounded?: number;
    borderWidth?: number;
    borderColor?: string;
    borderSolid: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    svgPath: string;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    outShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    inShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    outlineColorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isShowIndicator?: boolean;
    isScale?: boolean;
    scale?: number;

    // unused
    gradientRadius?: number;
    gradientCx?: number;
    gradientCy?: number;
    gradientFx?: number;
    gradientFy?: number;
    startOffset?: number;
    stopOffset?: number;
    [key: string]: any;
  }
>;

export class CustomSvgUtil extends BaseBoxShapeUtil<CustomSvgUtilType> {
  private shapeUtilHelper: ShapeUtilHelper;

  constructor(editor: Editor) {
    super(editor);
    this.shapeUtilHelper = new ShapeUtilHelper(editor);
  }

  static override type = "customSvg" as const;
  static override props: any;

  getDefaultProps() {
    return {
      w: 100,
      h: 100,
      fill: "#e3e3e3",
      outlineColor: "#000000",
      dash: "solid",
      transparency: 1,
      borderWidth: 0,
      borderColor: "#000000",
      strokeDasharray: "none",
      strokeWidth: 0,
      filterBlur: 0,
      borderSolid: "solid" as "solid" | "dashed",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      rounded: 0,
      svgPath: "",
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      colorMix: {
        "0": { color: "#e3e3e3", isHidden: false },
      },
      outlineColorMix: {},
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isShowIndicator: true,
      isScale: false,
      scale: defaultScale,
    };
  }

  getGeometry(shape: CustomSvgUtilType) {
    return this.shapeUtilHelper.calculateAdjustedDimensions(shape);
  }

  hideSelectionBoundsFg(_shape: CustomSvgUtilType): boolean {
    return false; // outline visibility is controlled by isChangingStyle in useHideSelectionOnChange
  }
  hideSelectionBoundsBg(shape: CustomSvgUtilType): boolean {
    return false; // Always show selection bounds regardless of opacity
  }
  hideResizeHandles(shape: CustomSvgUtilType): boolean {
    return false; // Always show resize handles regardless of opacity
  }
  isAspectRatioLocked(shape: CustomSvgUtilType): boolean {
    return !!shape?.props?.isScale;
  }
  onResize(shape: CustomSvgUtilType, info: TLResizeInfo<CustomSvgUtilType>) {
    if (shape?.props?.isScale) {
      return this.shapeUtilHelper.resizeScaled(shape, info);
    } else {
      return this.shapeUtilHelper.resizeDefault(shape, info);
    }
  }

  component(shape: CustomSvgUtilType) {
    const {
      w,
      h,
      outlineColor,
      dash,
      transparency,
      strokeWidth = 0,
      svgPath,
      borderWidth,
      strokeDasharray,
      filterBlur,
      borderSolid,
      isShowBorderTop,
      isShowBorderBottom,
      isShowBorderLeft,
      isShowBorderRight,
      rounded,
      outShadowArray,
      inShadowArray,
      colorMix,
      outlineColorMix,
      isHiddenLayerBlur,
      isHiddenBorder,
    } = shape.props;

    // Memoized derivations — see CustomRectUtil for the rationale. Cached on
    // the source prop references so a drag (only x/y changes) doesn't re-run
    // `Object.values().filter().map().mixColors()` per shape per pointer move.
    // `visibleColors` (used below for gradient stops) is recomputed inline
    // since the SVG-building block uses it as an array directly; the cost is
    // the same `Object.values().filter()` but without the `mixColors` step.
    const visibleColors = Object.values(colorMix).filter((c) => !c.isHidden);
    const calculatedFill = React.useMemo(() => {
      const visible = Object.values(colorMix).filter((c) => !c.isHidden);
      return visible.length > 0 ? mixColors(visible.map((c) => c.color)) : "transparent";
    }, [colorMix]);

    const calculatedOutlineColor = React.useMemo(() => {
      const visible = Object.values(outlineColorMix).filter((c) => !c.isHidden);
      return visible.length > 0 ? mixColors(visible.map((c) => c.color)) : "transparent";
    }, [outlineColorMix]);

    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgPath, "image/svg+xml");
    const svgElement = svgDoc.querySelector("svg");
    // Skip paths that are explicitly invisible (e.g. Tabler outline SVGs ship
    // with a fill="none" stroke="none" bounding rect as the first path) so the
    // stroke overlay traces the real icon, not the container box.
    const meaningfulPaths = Array.from(svgDoc.querySelectorAll("path")).filter((p) => {
      const f = (p.getAttribute("fill") || "").trim().toLowerCase();
      const s = (p.getAttribute("stroke") || "").trim().toLowerCase();
      return !(f === "none" && s === "none");
    });
    const pathData = meaningfulPaths
      .map((p) => p.getAttribute("d") || "")
      .filter(Boolean)
      .join(" ");

    // Capture the original viewBox for use in stroke overlay
    let originalViewBox = "0 0 100 100";

    if (svgElement) {
      // Preserve or create viewBox before changing dimensions to ensure proper scaling
      if (!svgElement.hasAttribute("viewBox")) {
        const originalWidth = svgElement.getAttribute("width") || "100";
        const originalHeight = svgElement.getAttribute("height") || "100";
        originalViewBox = `0 0 ${originalWidth} ${originalHeight}`;
        svgElement.setAttribute("viewBox", originalViewBox);
      } else {
        // Capture existing viewBox
        originalViewBox = svgElement.getAttribute("viewBox") || "0 0 100 100";
      }

      // Ensure the SVG scales properly maintaining aspect ratio
      svgElement.setAttribute("preserveAspectRatio", "xMidYMid meet");

      svgElement.setAttribute("width", "100%");
      svgElement.setAttribute("height", "100%");

      // Outline-style SVGs (e.g. Tabler) come with fill="none" and a stroke
      // attribute on the root — for those, the user's color must apply to the
      // stroke or every path floods solid. Filled-path SVGs (legacy) keep the
      // original behavior.
      const rootFill = (svgElement.getAttribute("fill") || "").trim().toLowerCase();
      const rootStroke = (svgElement.getAttribute("stroke") || "").trim();
      const isOutlineSvg = rootFill === "none" && rootStroke !== "" && rootStroke.toLowerCase() !== "none";

      if (isOutlineSvg) {
        svgElement.setAttribute("fill", "none");
        svgElement.setAttribute("stroke", String(calculatedFill));
        // Preserve the source stroke-width — Tabler ships "2" on a 24x24 viewBox.
      } else {
        svgElement.setAttribute("fill", String(calculatedFill));
        svgElement.setAttribute("stroke", "none");
        svgElement.setAttribute("stroke-width", "0");
      }
      svgElement.setAttribute("opacity", String(transparency));
      // Add inline styles to ensure SVG fills container properly
      svgElement.setAttribute("style", "display: block; width: 100%; height: 100%;");
      // svgElement.setAttribute("rx", String(rounded));

      const defs = svgDoc.createElementNS("http://www.w3.org/2000/svg", "defs");

      // Gradient definition
      if (visibleColors.length > 0) {
        const linearGradient = svgDoc.createElementNS("http://www.w3.org/2000/svg", "linearGradient");
        linearGradient.setAttribute("id", `gradient-${shape.id}`);
        linearGradient.setAttribute("x1", "0%");
        linearGradient.setAttribute("y1", "0%");
        linearGradient.setAttribute("x2", "100%");
        linearGradient.setAttribute("y2", "100%");

        visibleColors.forEach((colorObj, index) => {
          const stop = svgDoc.createElementNS("http://www.w3.org/2000/svg", "stop");
          stop.setAttribute("offset", `${(index / (visibleColors.length - 1)) * 100}%`);
          stop.setAttribute("stop-color", colorObj.color);
          linearGradient.appendChild(stop);
        });

        defs.appendChild(linearGradient);
      }

      // Shadows are rendered as CSS `filter: drop-shadow(...)` on the outer wrapper
      // (not as inline SVG <filter>s) so the shadow isn't clipped by the wrapper's
      // overflow: hidden. Inner shadow is intentionally not supported on icons.

      svgElement.insertBefore(defs, svgElement.firstChild);
    }

    const serializer = new XMLSerializer();
    const modifiedSvgPath = serializer.serializeToString(svgDoc);

    // Subscribe only to the *boolean* "is this shape hovered" — see
    // CustomRectUtil. Avoids O(N) re-renders on every hover transition.
    const editor = this.editor;
    const isHovered = useValue("isHovered", () => editor.getHoveredShapeId() === shape.id, [editor, shape.id]);

    // Build the composed CSS filter chain. Order: blur first, then drop-shadow
    // (so the shadow is cast off the already-blurred shape, not the other way
    // around). Inner shadow is intentionally unsupported for icons.
    const dropShadowChain = shadowsToCssFilterDropShadow(outShadowArray as any);
    const blurFilter = !isHiddenLayerBlur && filterBlur && filterBlur > 0 ? `blur(${filterBlur}px)` : "";
    const composedFilter = [blurFilter, dropShadowChain].filter(Boolean).join(" ") || undefined;

    return (
      <>
        <div
          style={{
            borderTop:
              isShowBorderTop && !isHiddenBorder
                ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
                : "none",
            borderBottom:
              isShowBorderBottom && !isHiddenBorder
                ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
                : "none",
            borderLeft:
              isShowBorderLeft && !isHiddenBorder
                ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
                : "none",
            borderRight:
              isShowBorderRight && !isHiddenBorder
                ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
                : "none",
            borderRadius: `${rounded}px`,
            filter: composedFilter,
            width: w,
            height: h,
            position: "relative",
            overflow: "hidden",
          }}
          id={shape.id}
          className="relative"
          dangerouslySetInnerHTML={{ __html: modifiedSvgPath }}
        />
        {/* Stroke overlay - rendered separately to not affect inner element */}
        {strokeWidth && strokeWidth > 0 && pathData ? (
          <svg
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: w,
              height: h,
              pointerEvents: "none",
            }}
            viewBox={originalViewBox}
          >
            <path
              d={pathData}
              fill="none"
              stroke={Object.entries(shape.props.outlineColorMix).length > 0 ? calculatedOutlineColor : outlineColor}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray || "none"}
              strokeOpacity={transparency}
              strokeLinecap="round"
            />
          </svg>
        ) : null}
        {isHovered ? (
          <svg
            style={{
              fill: "none",
              stroke: HOVER_EFFECT.COLOR,
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
            }}
          >
            {this.indicator(shape)}
          </svg>
        ) : null}
      </>
    );
  }

  addDefaultColorMix(shape: CustomSvgUtilType) {
    this.shapeUtilHelper.addDefaultColorMix(shape);
  }
  addDefaultOutShadow(shape: CustomSvgUtilType) {
    this.shapeUtilHelper.addDefaultOutShadow(shape);
  }
  addDefaultInShadow(shape: CustomSvgUtilType) {
    this.shapeUtilHelper.addDefaultInShadow(shape);
  }

  indicator(shape: CustomSvgUtilType) {
    const { w, h } = shape.props;

    // Simple rectangle indicator around the shape bounds
    // No need to render the full SVG path as indicator
    return <rect x={0} y={0} width={w} height={h} />;
  }
}
